import React, { useState } from "react";
import { Link } from "react-router-dom";
import Pagination from "../common/Pagination";
import { ClearIcon, ClearSearchIcon, Warning } from "../common/ProjectIcons";
import TextInput from "../common/TextInput";
import { Video } from "../common/ObjectTypes";
import ThumbnailModal from "../common/ThumbnailModal";
import SelectInput from "../common/SelectInput";
import VideoSearchModal from "./VideoSearchModal";
import tailwindClassnames from "../common/TaildwindClassnames";
import { Tooltip } from "@mui/material";
import { MoonLoader } from "react-spinners";

type VideosListProps = {
  videoResults: Video[];
  sortVideosName: Function;
  sortVideosDate: Function;
  sortVideosPublished: Function;
  onChangeFilter: Function;
  onSelectCountry: Function;
  onChangeSpecies: Function;
  onChangeResolution: Function;
  onChangeStatus: Function;
  onSearch: Function;
  onSelectVideo: Function;
  onOpenSearchModal: Function;
  onCloseSearchModal: Function;
  isModalOpen: boolean;
  subStr: any;
  pageCount: number;
  pageSize: number;
  totalCount: number;
  handlePageClick: (selectedItem: { selected: number }) => void;
  reloadPage: boolean;
  recordStart: number;
  recordEnd: number;
  selectVideosPage: boolean;
  photoUrl: string;
  onClear: Function;
  onReset: Function;
  isVideoIncluded?: Function;
  onChangeVideoSelection?: Function;
  isCatalogueVideoIncluded?: Function;
  onSelectVideoForCatalogue?: Function;
  onSelectVideoForMapMarker?: Function;
  isMapMarkerVideoIncluded?: Function;
  onSelectAllVideos?: Function;
  areAllVideosSelected?: boolean;
  isLoadingForCatalogueSelectAll?: boolean;
  countries: any[];
  species: any[];
  resolutions: any[];
  statuses: any[];
  searchQuery: any;
  loading: boolean;
  organizations: any[];
  isDisabled?: boolean;
  tags: any[];
  onSelectTags: Function;
};

const VideosList = ({
  videoResults,
  sortVideosName,
  sortVideosDate,
  sortVideosPublished,
  onChangeFilter,
  onCloseSearchModal,
  onOpenSearchModal,
  isModalOpen,
  onSelectCountry,
  onChangeSpecies,
  onChangeResolution,
  onChangeStatus,
  onSearch,
  onSelectVideo,
  subStr,
  pageCount,
  pageSize,
  totalCount,
  handlePageClick,
  reloadPage,
  recordStart,
  recordEnd,
  photoUrl,
  selectVideosPage,
  onClear,
  onReset,
  isVideoIncluded,
  onSelectVideoForCatalogue,
  isCatalogueVideoIncluded,
  onSelectVideoForMapMarker,
  isMapMarkerVideoIncluded,
  onChangeVideoSelection,
  onSelectAllVideos,
  areAllVideosSelected,
  isLoadingForCatalogueSelectAll,
  countries,
  species,
  resolutions,
  statuses,
  searchQuery,
  loading,
  organizations,
  isDisabled,
  tags,
  onSelectTags,
}: VideosListProps) => {
  const [videoOrderByName, setVideoOrderByName] = useState(false);
  const [videoOrderByEditedDate, setVideoOrderByEditedDate] = useState(true);
  const [videoOrderByPublished, setVideoOrderByPublished] = useState(false);
  const [thumbnailModal, setThumbnailModal] = useState(false);
  const [selectedThumbnailUrl, setSelectedThumbnailUrl] = useState("");
  // Get timestamp from localStorage to prevent caching
  const thumbnailUpdateTimestamp =
    localStorage.getItem("thumbnail_last_updated") || Date.now().toString();

  const handleThumbnailClick = (thumbnailUrl: string) => {
    setSelectedThumbnailUrl(thumbnailUrl);
    setThumbnailModal(true);
  };

  const handleCloseThumbnailModal = () => {
    setThumbnailModal(false);
  };

  const getCountryNameById = (code: any) => {
    let countryName = countries?.find((country) => country.id === code);
    return countryName?.name || null;
  };

  const defaultCountryName = getCountryNameById(subStr.countryObtainedId);

  const getResolutionNameById = (code: any) => {
    let resName = resolutions?.find((res) => res.videoResolutionId === code);
    return resName?.name || null;
  };

  const defaultResolutionName = getResolutionNameById(subStr.videoResolutionId);

  const getSpeciesNameById = (speciesId: any) => {
    let speciesObj = species?.find(
      (species) => species.speciesId === speciesId
    );
    return speciesObj?.name || "";
  };

  const defaultSpeciesNames = subStr.speciesIds.map((speciesId: any) =>
    getSpeciesNameById(speciesId)
  );

  const getTagNameById = (tagId: any) => {
    let tagObj = tags?.find((tag) => tag.tagId === tagId);
    return tagObj?.name || "";
  };

  const defaultTagNames = subStr.tagIds.map((tagId: any) =>
    getTagNameById(tagId)
  );

  const getStatusNameById = (statusId: any) => {
    let statusObj = statuses?.find(
      (status) => status.videoStatusId === statusId
    );
    return statusObj?.name || "";
  };

  const defaultStatusNames = subStr.videoStatusIds.map((statusId: any) =>
    getStatusNameById(statusId)
  );

  const getOrgById = (orgId: any) => {
    let orgObj = organizations?.find((org) => org.organizationId === orgId);
    return orgObj?.name || "";
  };
  return (
    <div className="p-6 space-y-6">
      {thumbnailModal && (
        <ThumbnailModal
          thumbnailUrl={selectedThumbnailUrl}
          onClose={handleCloseThumbnailModal}
        />
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-gray-900">Videos</h1>
        <div className="flex items-center gap-2">
          {selectVideosPage && onSelectAllVideos && (
            <button
              onClick={() => onSelectAllVideos()}
              disabled={isLoadingForCatalogueSelectAll || isDisabled}
              className={`px-4 py-2 text-sm font-medium text-white ${
                isLoadingForCatalogueSelectAll || isDisabled
                  ? "bg-gray-400"
                  : "bg-tealBlue-400 hover:bg-tealBlue-600"
              } rounded-full transition`}
            >
              {isLoadingForCatalogueSelectAll ? (
                <span className="flex items-center">
                  <MoonLoader color="#FFFFFF" size={16} />
                  <span className="ml-2">Loading...</span>
                </span>
              ) : areAllVideosSelected ? (
                "Deselect All"
              ) : (
                "Select All"
              )}
            </button>
          )}
          <button
            onClick={() => onOpenSearchModal()}
            className="px-4 py-2 text-sm font-medium text-white bg-tealBlue-400 rounded-full hover:bg-tealBlue-600 transition"
          >
            Search
          </button>
          <VideoSearchModal
            isOpen={isModalOpen}
            onClose={onCloseSearchModal}
            onSearch={onSearch}
            onClear={onReset}
            defaultCountryName={defaultCountryName}
            defaultSpeciesNames={defaultSpeciesNames}
            defaultTagNames={defaultTagNames}
            defaultResolutionName={defaultResolutionName}
            defaultStatusNames={defaultStatusNames}
            countries={countries}
            species={species}
            resolutions={resolutions}
            statuses={statuses}
            subStr={subStr}
            onChangeSpecies={onChangeSpecies}
            onSelectCountry={onSelectCountry}
            onChangeFilter={onChangeFilter}
            onChangeResolution={onChangeResolution}
            onChangeStatus={onChangeStatus}
            tags={tags}
            onChangeTags={onSelectTags}
          />
          <button
            onClick={() => onClear()}
            className="p-2 text-gray-500 hover:text-gray-700 rounded-full bg-gray-100 hover:bg-gray-200 transition"
          >
            <ClearSearchIcon />
          </button>
          {!selectVideosPage && (
            <Link
              to="/video/add"
              className="px-4 py-2 text-sm font-medium text-white bg-tealBlue-400 rounded-full hover:bg-tealBlue-600 transition"
            >
              Add
            </Link>
          )}
        </div>
      </div>

      {/* Video Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
        {videoResults.map((video: Video) => (
          <div
            key={video.videoId}
            onClick={() =>
              !selectVideosPage ? onSelectVideo(video.videoId) : null
            }
            className="group rounded-2xl shadow-sm border border-gray-200 bg-white hover:shadow-lg transition overflow-hidden flex flex-col h-full"
          >
            {/* Thumbnail */}
            <div
              className="aspect-video w-full overflow-hidden relative"
              onClick={(e) => {
                if (!selectVideosPage) {
                  e.stopPropagation();
                  handleThumbnailClick(video.thumbnailUrl);
                }
              }}
            >
              {selectVideosPage && (
                <div className="absolute inset-0 flex items-start p-2 z-10 bg-black bg-opacity-20">
                  <input
                    onChange={(event) => {
                      const isChecked = event.target.checked;

                      if (onSelectVideoForCatalogue)
                        onSelectVideoForCatalogue(video, isChecked);
                      else if (onSelectVideoForMapMarker)
                        onSelectVideoForMapMarker(video, isChecked);
                    }}
                    checked={
                      (isCatalogueVideoIncluded &&
                        isCatalogueVideoIncluded(video.videoId)) ||
                      (isMapMarkerVideoIncluded &&
                        isMapMarkerVideoIncluded(video.videoId))
                    }
                    type="checkbox"
                    id={`checkbox-${video.videoId}`}
                    className="w-4 h-4 cursor-pointer"
                    disabled={isDisabled}
                  />
                </div>
              )}
              <img
                src={`${photoUrl}${video.thumbnailUrl}?t=${thumbnailUpdateTimestamp}`}
                alt={video.name}
                className="object-cover w-full h-full group-hover:scale-105 transition-transform"
              />
            </div>

            {/* Content */}
            <div className="p-4 flex flex-col flex-grow cursor-pointer">
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-lg font-medium text-gray-900 truncate">
                  {video.name}
                </h2>{" "}
                {video.tags &&
                  video.tags.some((tag) =>
                    [
                      "df4966f6-e7fc-4afd-55fc-08dc08934998",
                      "d47592e1-5fd7-49b8-55fd-08dc08934998",
                      "b1a28d55-d3d4-41a4-55fe-08dc08934998",
                      "3c09ac77-bec2-4902-55ff-08dc08934998",
                    ].includes(tag.tagId)
                  ) && (
                    <div className="flex flex-row">
                      <Tooltip
                        title="This video contains graphic content."
                        arrow
                        slotProps={{ tooltip: { sx: { fontSize: ".75em" } } }}
                      >
                        <span className="ml-1 inline-flex items-center justify-center w-4 h-4 text-xs text-orange-700">
                          <Warning />
                        </span>
                      </Tooltip>
                    </div>
                  )}
              </div>

              <p className="text-sm text-gray-600 line-clamp-3">
                {video.overview}
              </p>

              {/* Add some space between overview and org/country */}
              <div className="h-2"></div>

              {/* Organization and country at the bottom of the card */}
              <div className="flex items-center justify-between text-xs text-gray-500 pt-2 mt-auto">
                <span className="font-bold">
                  {getOrgById(video.organizationId)}
                </span>
                <span>
                  {
                    countries.find((c) => c.id === video.countryObtainedId)
                      ?.name
                  }
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Pagination */}
      <Pagination
        handlePageClick={handlePageClick}
        pageCount={pageCount}
        recordStart={recordStart}
        recordEnd={recordEnd}
        totalCount={totalCount}
        loading={loading}
        pageSize={searchQuery.pageSize}
        selectPage={selectVideosPage || false}
        searchQuery={searchQuery}
      />
    </div>
  );
};

export default VideosList;
